import { countries as countriesData } from 'localizations';
import { sortBy } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { CLUSTER, DEFAULT_LOCALE, SelectOptionType } from '@/shared/config';
import { debug, getCountryName } from '@/shared/lib';

type Clusters = 'ca' | 'eu' | 'oceania' | 'ru' | 'us';
type ClustersOptions = {
  sign_up_allowed_countries: string[];
  all_countries: string[];
  url: string;
  languages: string[];
};

type ClusterConfig = Record<Clusters, ClustersOptions>;

export const getClusterPrefix = () => window.origin.split('.').pop();

type UseCountries = {
  // Control from 'react-hook-form'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  fieldName?: string;
};

const getClustersConfig = async (): Promise<ClusterConfig> =>
  import('localizations').then((localizations) => localizations.clusters);

/**
 * Transforms countries data into SelectOption format with localized names.
 * Uses shared getCountryName utility to avoid code duplication.
 */
const transformCountries = (languageKey = DEFAULT_LOCALE): SelectOptionType[] =>
  sortBy(
    Object.keys(countriesData).map((countryKey) => ({
      value: countryKey,
      label: getCountryName(countryKey, languageKey),
    })),
    'label',
  );

export const useCountries = ({
  control,
  fieldName = 'language',
}: UseCountries) => {
  const [countries, setCountries] = useState<SelectOptionType[]>([]);

  const cluster = getClusterPrefix();

  const language = useWatch({
    control,
    name: fieldName,
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const allClustersConfig = await getClustersConfig();
        const clusterConfig = allClustersConfig[CLUSTER as Clusters];
        const availableCountries = clusterConfig?.sign_up_allowed_countries;

        const allCountries = transformCountries(language);

        if (clusterConfig && availableCountries?.length > 0) {
          setCountries(
            allCountries.filter((country: SelectOptionType) =>
              availableCountries.includes(country.value),
            ),
          );
        } else {
          setCountries(allCountries);
        }
      } catch (error: unknown) {
        debug(error);
      }
    };

    fetchCountries();
  }, [cluster, language]);

  return countries;
};

export const useCountriesOptions = (
  locale: string | undefined,
): SelectOptionType[] => {
  const [countries, setCountries] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    try {
      setCountries(transformCountries(locale));
    } catch (error: unknown) {
      debug(error);
    }
  }, [locale]);

  return countries;
};
